import type { BusbarEvidenceResponse } from "../services/busbarEvidence";
import { formatQuantity } from "../utils/formatQuantity";
import {
  STATUS_LABELS as UPS_STATUS_LABELS,
  STATUS_TONE as UPS_STATUS_TONE,
} from "./UPSResultSummary";
import { StatusIcon } from "./StatusIcon";
import { bannerTone } from "./statusTone";
import { BUSBAR_KIND_LABELS, BUSBAR_MATERIAL_LABELS } from "./busbarEvidenceDraft";

// The same words and the same colour as the Transformer, DG, UPS, PV, starter and
// breaker summaries for a calculated, a warned and a review-required study: missing
// evidence is a review the engineer owes, not a design failure. A failed check is a
// failure. The maps are the UPS summary's, typed here against the busbar evidence
// status so a value only this module gains cannot pass without words and a colour;
// the NO_SOLUTION words are this module's own, because "no standard rating fits" is
// not what a failed busbar check means.
const STATUS_LABELS: Record<BusbarEvidenceResponse["status"], string> = {
  ...UPS_STATUS_LABELS,
  NO_SOLUTION: "Busbar evidence not met",
};

const STATUS_TONE: Record<BusbarEvidenceResponse["status"], "pass" | "fail" | "warning"> =
  UPS_STATUS_TONE;

/** "Busbar evidence not met" for a failed check; the other statuses' own words. */
export function statusHeading(result: BusbarEvidenceResponse): string {
  return STATUS_LABELS[result.status];
}

type BusbarEvidenceResultSummaryProps = {
  result: BusbarEvidenceResponse;
};

// The decisive values first (Master Prompt v2.1 section 19): what busbar was held
// against what fault level, how far the design current sits inside the rated current,
// the Icw and Ipk margins with the durations they were held over, the neutral bar's
// loading when a pair was given and the trunking voltage drop for a trunking study.
// No figure is calculated here: every value is the engine's, echoed or picked; a
// check the engine did not make shows an empty quantity. Display rule A12: four
// significant figures on screen, the exact engine decimal in the tooltip (title).
export function BusbarEvidenceResultSummary({ result }: BusbarEvidenceResultSummaryProps) {
  const tone = STATUS_TONE[result.status];

  const loading = formatQuantity(result.loading_percent, "%");
  const faultLevel = formatQuantity(result.prospective_current_ka, "kA");
  const peak = formatQuantity(result.peak_current_ka, "kA");
  const icwMargin = formatQuantity(result.icw_margin_ka, "kA");
  const ipkMargin = formatQuantity(result.ipk_margin_ka, "kA");
  const neutralLoading = formatQuantity(result.neutral_loading_percent, "%");
  const dropPercent = formatQuantity(result.voltage_drop_percent, "%");
  const dropVolts = formatQuantity(result.voltage_drop_v, "V");

  return (
    <section aria-label="Result summary" data-summary-tone={tone}>
      <header>
        <p data-summary-status={result.status}>
          <StatusIcon tone={bannerTone(result.status, STATUS_TONE)} size={20} />
          {statusHeading(result)}
        </p>
        <p>
          Study <strong>{result.code}</strong> · {result.name} ·{" "}
          {result.jurisdiction_profile} profile
        </p>
      </header>
      <dl data-summary-strip>
        <div>
          <dt>Busbar</dt>
          {/* The kind and the material as chosen; words, not quantities. */}
          <dd data-summary-field="busbar" data-summary-value="text">
            {BUSBAR_KIND_LABELS[result.kind]} · {BUSBAR_MATERIAL_LABELS[result.material]}
          </dd>
        </div>
        <div>
          <dt>Loading</dt>
          {/* Design current over the builder's rated current at site conditions, as the
              engine reported it; the two currents are echoed as typed. */}
          <dd data-summary-field="loading" data-summary-value="text" title={loading.exact ?? undefined}>
            {loading.display} ({result.design_current_a} of {result.declared_rated_current_a} A)
          </dd>
        </div>
        <div>
          <dt>Fault level</dt>
          <dd data-summary-field="fault-level" title={faultLevel.exact ?? undefined}>
            {faultLevel.display} at {result.fault_bus_code}
          </dd>
        </div>
        <div>
          <dt>Peak current</dt>
          {/* ip as the fault level holds it; empty when it was not held, so the peak
              withstand check was not made. */}
          <dd data-summary-field="peak-current" title={peak.exact ?? undefined}>
            {peak.display}
          </dd>
        </div>
        <div>
          <dt>Icw margin</dt>
          {/* Icw - Ik'', negative when the check failed, over the required fault duration
              against the duration Icw is rated for; both durations echoed as typed. */}
          <dd data-summary-field="icw-margin" data-summary-value="text" title={icwMargin.exact ?? undefined}>
            {icwMargin.display} ({result.required_fault_duration_s} s of{" "}
            {result.short_time_withstand_duration_s} s)
          </dd>
        </div>
        <div>
          <dt>Ipk margin</dt>
          {/* Ipk - ip; empty when the busbar was not held against a peak. */}
          <dd data-summary-field="ipk-margin" title={ipkMargin.exact ?? undefined}>
            {ipkMargin.display}
          </dd>
        </div>
        {result.neutral !== null ? (
          <div>
            <dt>Neutral loading</dt>
            <dd data-summary-field="neutral-loading" title={neutralLoading.exact ?? undefined}>
              {neutralLoading.display}
            </dd>
          </div>
        ) : null}
        {result.trunking !== null ? (
          <div>
            <dt>Voltage drop</dt>
            {/* The line-to-line drop over the route, as a share of the system voltage and
                in volts; both the engine's. */}
            <dd
              data-summary-field="voltage-drop"
              title={[dropPercent.exact, dropVolts.exact].filter(Boolean).join(" · ") || undefined}
            >
              {dropPercent.display} ({dropVolts.display})
            </dd>
          </div>
        ) : null}
        <div>
          <dt>Warnings</dt>
          <dd data-summary-warnings={result.warnings.length}>{result.warnings.length}</dd>
        </div>
      </dl>
    </section>
  );
}

export { STATUS_LABELS, STATUS_TONE };
